/**
 * @description Serviço para renovação do token do Google
 * @module RefreshTokenGoogle
 * @since Fevereiro 2024
 */

import axios from "axios";
import { endpoints } from "../../configs/Settings";
import { useAuth } from "@/utils/useAuth";

interface GoogleTokenResponse {
  access_token: string;
  expires_in: number;
  scope: string;
  token_type: string;
}

interface RefreshTokenGoogleOptions {
  clientId?: string;
  clientSecret?: string;
  /**
   * Cliente OAuth público (Android/iOS): sem client_secret.
   * Troca direto no Google (PKCE / installed app).
   */
  publicClient?: boolean;
}

/**
 * Renova o token de acesso do Google usando o refresh token
 * @param refreshToken - O refresh token do Google
 * @returns Promise com o novo access token e informações relacionadas
 * @throws Error se a renovação falhar
 */
export async function RefreshTokenGoogle(
  refreshToken: string,
  options?: RefreshTokenGoogleOptions
): Promise<GoogleTokenResponse> {
  try {
    const clientId = options?.clientId || endpoints.GOOGLE_CLIENT_ID;
    const formData = new URLSearchParams();
    formData.append("client_id", clientId);
    formData.append("grant_type", "refresh_token");
    formData.append("refresh_token", refreshToken);

    // Cliente Web (confidencial): secret + proxy Fluxmed
    // Cliente mobile (público): sem secret, direto no Google
    if (options?.publicClient) {
      const response = await axios.post(
        "https://oauth2.googleapis.com/token",
        formData,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (!response.data.access_token) {
        throw new Error("Token de acesso não recebido");
      }
      return response.data;
    }

    formData.append(
      "client_secret",
      options?.clientSecret || endpoints.GOOGLE_CLIENT_SECRET
    );

    const response = await axios.post(
      `${endpoints.GOOGLE_AUTH}/token`,
      formData,
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: await useAuth.getUserToken(),
        },
      }
    );

    if (!response.data.access_token) {
      throw new Error("Token de acesso não recebido");
    }

    return response.data;
  } catch (error) {
    console.error("Erro ao renovar token do Google:", error);
    throw error instanceof Error
      ? error
      : new Error("Falha ao renovar token do Google");
  }
}
